



















































































































































































































































































import {
  computed,
  defineComponent,
  nextTick,
  PropType,
  ref
} from '@vue/composition-api';
import {
  Group,
  groupsProvider
} from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { useVModel } from '@vueuse/core';
import { debounce } from 'lodash';

import {
  IWorkflowState,
  IWorkflowStateData,
  IWorkflowStateReminderConfigData,
  WorkflowState,
  WorkflowStateAssignmentTypeEnum
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { PromiseQueue } from '@/models/promise-queue/promise-queue';

import { IRootState } from '@/store';
import {
  IWorkflowStoreState,
  IWorkflowGetters,
  IWorkflowActions,
  IWorkflowMutations
} from '@/store/workflow';

import {
  diffDeep,
  getPropertyFromItem,
  setErrorFromAxiosError
} from '@/utils/helpers';
import { requiredValidator } from '@/utils/validators';
import { SystemError } from '@/models/error/error';
import { ALForm } from '@/common/component.types';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import { VForm } from 'vuetify/lib/components';
import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import WorkflowStateThresholdsGroupList from '@/components/workflows/WorkflowStateThresholdsGroupList.vue';
import WorkflowStateReminderConfigFormFields from './WorkflowStateReminderConfigFormFields.vue';

export default defineComponent({
  name: 'WorkflowStateForm',

  components: {
    ALDialog,
    GroupAutoCompletePicker,
    WorkflowStateThresholdsGroupList,
    WorkflowStateReminderConfigFormFields
  },

  props: {
    workflowId: { type: Number, required: true },
    value: { type: Object as PropType<IWorkflowStateData>, required: true },
    disabled: {
      type: Boolean
    }
  },

  setup(props, { emit }) {
    const workflowStateData = useVModel(props, 'value');

    const promiseQueue = new PromiseQueue();

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowStoreState,
      IWorkflowGetters,
      IWorkflowActions,
      IWorkflowMutations
    >(store, 'workflow');

    const {
      getWorkflowStateById,
      getWorkflowStateAssignmentTypeVisuals
    } = useGetters([
      'getWorkflowStateById',
      'getWorkflowStateAssignmentTypeVisuals'
    ]);

    // workflowStateData.enable_reminder

    const { updateWorkflowState } = useActions(['updateWorkflowState']);

    const isFormValid = ref(false);
    const formRef = ref<InstanceType<typeof VForm> & ALForm>();

    const error = ref<ISystemError | null>(null);

    function clearError() {
      error.value = null;
    }

    const isLoading = ref(false);

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    const groupsStoreProvider = computed<any>(() => groupsProvider(store));

    const getGroupById = computed<(id: number) => Group>(
      () => groupsStoreProvider.value.getGroupById
    );

    const computedGroups = computed(() => {
      return (
        workflowStateData.value!.groups!.map(group => {
          return new Group({
            id: getPropertyFromItem(group as Group, 'id', group),
            name: getGroupById.value(
              getPropertyFromItem(group as Group, 'id', group)
            )?.name
          });
        }) ?? []
      );
    });

    function updateGroup(group: number) {
      // group may be a number or an object depending if the value is coming from the server or locally
      const groupIndex = workflowStateData.value.groups!.findIndex(
        permissionGroup =>
          group ===
          getPropertyFromItem(permissionGroup as Group, 'id', permissionGroup)
      );

      // if tag not currently in tag list as to not add duplicates
      if (groupIndex === -1) {
        workflowStateData.value.groups!.push(group);
      }

      debounceSaveWorkflowState();

      nextTick(() => {
        groupAutoCompletePickerRef.value?.reset();
      });
    }

    function removeGroup(groupId: number) {
      // group may be a number or an object depending if the value is coming from the server or locally
      const groupIndex = workflowStateData.value.groups!.findIndex(
        permissionGroup =>
          groupId ===
          getPropertyFromItem(permissionGroup as Group, 'id', permissionGroup)
      );

      workflowStateData.value.groups!.splice(groupIndex, 1);

      // save state
      debounceSaveWorkflowState();
    }

    async function saveWorkflowState() {
      clearError();

      const valid = validateForm();

      if (valid) {
        isLoading.value = true;
        let workflowStateInstance: IWorkflowState | null = null;

        try {
          workflowStateInstance = new WorkflowState(workflowStateData.value);
        } catch (err) {
          if (SystemError.isSystemError(err)) {
            error.value = err;
          }
        }
        if (workflowStateInstance != null) {
          const payloadDelta = diffDeep(
            workflowStateInstance,
            getWorkflowStateById.value(workflowStateInstance.id!),
            true
          );
          if (payloadDelta.groups) {
            payloadDelta.groups = payloadDelta.groups.map(group =>
              typeof group === 'number' ? group : group.id!
            );
          }

          updateWorkflowState({
            workflowId: props.workflowId!,
            state: payloadDelta
          })
            .then(() => {
              reminderDialog.value = false;
              emit(
                'update-workflow-state-data',
                payloadDelta.id!.toString(),
                getWorkflowStateById.value(workflowStateInstance!.id!)
              );
            })
            .catch(err => {
              error.value = setErrorFromAxiosError(err);
            })
            .finally(() => {
              isLoading.value = false;
            });
        }
      }
    }
    const debounceSaveWorkflowState = debounce(() => {
      promiseQueue.clearQueue();

      promiseQueue.enqueue(() => saveWorkflowState());
    }, 1000);

    const groupAutoCompletePickerRef = ref<
      InstanceType<typeof GroupAutoCompletePicker>
    >();

    const isReminderFormValid = ref(false);
    const reminderBaseFormRef = ref<InstanceType<typeof VForm> & ALForm>();

    const reminderDialog = ref(false);

    const reminderConfigData = ref<IWorkflowStateReminderConfigData | null>(
      null
    );

    function openStateReminderDialog() {
      if (workflowStateData.value.reminder_config) {
        reminderConfigData.value = workflowStateData.value.reminder_config;
      } else {
        reminderConfigData.value = {
          interval_in_days: 1,
          email: {
            subject: '',
            body: ''
          }
        };
      }

      reminderDialog.value = true;
    }

    function saveReminderConfig() {
      clearError();

      const valid = reminderBaseFormRef.value?.validate();

      if (valid) {
        workflowStateData.value.reminder_config = reminderConfigData.value;
        return saveWorkflowState();
      }
    }
    return {
      formRef,
      groupAutoCompletePickerRef,

      workflowStateData,
      getWorkflowStateAssignmentTypeVisuals,
      isFormValid,
      isLoading,
      requiredValidator,
      error,
      clearError,
      computedGroups,
      WorkflowStateAssignmentTypeEnum,

      debounceSaveWorkflowState,
      updateGroup,
      removeGroup,

      reminderConfigData,
      reminderBaseFormRef,
      saveReminderConfig,
      isReminderFormValid,
      reminderDialog,
      openStateReminderDialog
    };
  }
});
